import { useCallback, useEffect, useRef, useState } from 'react';
import { getMyCreations } from '../services/myCreationsApi';

interface MyCreationsInfiniteScrollOptions {
  assetType?: 'Track' | 'Image' | 'Artifact';
  rootMargin?: string;
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  enabled?: boolean;
}

interface MyCreationsInfiniteScrollState {
  items: any[];
  loading: boolean;
  hasMore: boolean;
  error: string | null;
  total: number;
  loadMore: () => Promise<void>;
  reset: () => void;
  refresh: () => void;
}

export const useMyCreationsInfiniteScroll = ({
  assetType,
  enabled = true,
}: MyCreationsInfiniteScrollOptions): MyCreationsInfiniteScrollState => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const loadingRef = useRef(false);
  const offsetRef = useRef(0);
  const loadMoreRef = useRef<(() => Promise<void>) | null>(null);

  // Reset function
  const reset = useCallback(() => {
    setItems([]);
    setHasMore(true);
    setError(null);
    setTotal(0);
    offsetRef.current = 0;
    setInitialLoadDone(false);
  }, []);

  // Refresh function - reload without showing skeleton
  const refresh = useCallback(async () => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    setError(null);
    offsetRef.current = 0;

    try {
      const result = await getMyCreations({
        assetType,
        limit: 40,
        offset: 0,
      });

      setItems(result?.items || []);
      const hasNext = result?.pagination?.hasNext || false;
      const totalCount = result?.pagination?.total || 0;

      setHasMore(hasNext);
      setTotal(totalCount);
      offsetRef.current = result?.items?.length || 0;

      loadingRef.current = false;
      setLoading(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load creations';
      setError(errorMessage);
      loadingRef.current = false;
      setLoading(false);
    }
  }, [assetType]);

  // Load more function
  const loadMore = useCallback(async () => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await getMyCreations({
        assetType,
        limit: 40,
        offset: offsetRef.current,
      });

      if (offsetRef.current === 0) {
        // Initial load - replace items
        setItems(result?.items || []);
      } else {
        // Subsequent loads - append items
        setItems((prev) => [...prev, ...(result?.items || [])]);
      }

      const hasNext = result?.pagination?.hasNext || false;
      const totalCount = result?.pagination?.total || 0;

      setHasMore(hasNext);
      setTotal(totalCount);
      offsetRef.current += result?.items?.length || 0;

      // Set initial load done after first successful load
      if (!initialLoadDone) {
        setInitialLoadDone(true);
      }

      loadingRef.current = false;
      setLoading(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load creations';
      setError(errorMessage);
      loadingRef.current = false;
      setLoading(false);
    }
  }, [assetType]);

  // Store loadMore in ref for use in effects
  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  // Initial load
  useEffect(() => {
    if (!initialLoadDone && enabled && loadMoreRef.current) {
      loadMoreRef.current();
    }
  }, [enabled, initialLoadDone]);

  return {
    items,
    loading,
    hasMore,
    error,
    total,
    loadMore: loadMoreRef.current || (() => Promise.resolve()),
    reset,
    refresh,
  };
};

export default useMyCreationsInfiniteScroll;
